import {
  useCallback, useEffect, useRef, useState,
  useLayoutEffect,
} from 'react';
import { push } from 'redux-first-history';
import { debounce, isEmpty } from 'lodash';
import { hasFirebaseInstance } from '@client/selectors/helpers/clients';
import { getSessionUserAdminStatus, getUserId } from '@client/selectors/helpers/session';
import { setCurrentShow } from '@client/slices/entities/shows.reducer';
import {
  batch, useDispatch, useInstanceSelector, useSelector,
} from './redux.hooks';
import type { RootState } from '@client/store';
import type { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import type { SDK } from '@sda/sdk/helpers/sdk';

/**
 * Returns a static, debounced callback
 * @param  {Function} callback
 * @param  {number} wait
 * @param  {any[]} deps
 */
export const useDebounce = <T extends (...args: any) => any>(callback: (...args: Parameters<T>) => any, wait = 200, deps = []): T => useCallback(debounce(callback, wait), deps) as any; // eslint-disable-line

/**
 * Runs the selected effect on mount
 * @param  {Function} effect
 * @param  {boolean} [ready] - Set to false if this should be dleayed
 * @return {void}
 */
export const useMountEffect = (effect: React.EffectCallback, ready = true) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (ready) {
      if (!ref.current) {
        return effect();
      }

      ref.current = true;
    }

    return undefined;
  }, [ready]); // eslint-disable-line
};

type Permissions = 'moderator' | 'collaborator';

const usePermissionsCheck = (permissions?: Permissions) => {
  const userId = useSelector(getUserId);
  const isAdmin = useSelector(getSessionUserAdminStatus);

  return useCallback((document: { owner?: string, collaborators?: string[], moderators?: string[] }) => {
    if (!document) {
      return false;
    }

    if (isAdmin) {
      return true;
    }

    if (!permissions) {
      return true;
    }

    if (permissions === 'moderator') {
      return !!(userId
        && document
        && (document.owner === userId
          || (document.collaborators && document.collaborators.indexOf(userId) !== -1)
          || (document.moderators && document.moderators.indexOf(userId) !== -1)
        ));
    }

    return !!(userId && document && (document.owner === userId || (document.collaborators && document.collaborators.indexOf(userId) !== -1)));
  }, [isAdmin, userId, permissions]);
};

/*
 * Set the current show on load and reset on unmount

 */
export const useSetCurrentShow = (showId: string) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setCurrentShow(showId || null));

    return () => {
      dispatch(setCurrentShow(null));
    };
  }, [dispatch, showId]);
};

interface UseFirestoreOptions {
  readonly key?: string;
  readonly selector?: (state: RootState, ...args: any[]) => any;
  readonly upserter?: ActionCreatorWithOptionalPayload<unknown>;
  readonly enforcePermissions?: Permissions;
}

export const useFirestore = <T, C = SDK<T>[keyof SDK<T>]>(
  effect: (sdk: typeof window.Firebase.SDK) => Promise<C>,
  options: UseFirestoreOptions,
): [C, boolean] => {
  const {
    key, upserter, selector = () => false, enforcePermissions = null,
  } = options;

  const dispatch = useDispatch();
  const cachedData = useInstanceSelector(selector, key);
  const hasCachedData = Boolean(key && cachedData);

  const firestoreRef = useRef<typeof window.Firebase.SDK>(null);
  const prevKey = useRef(null);

  const [data, setData] = useState<C>(cachedData);
  const [isLoading, setIsLoading] = useState(!hasCachedData);

  const hasFirebaseLoaded = useSelector(hasFirebaseInstance);
  const handlePermissionsCheck = usePermissionsCheck(enforcePermissions);

  useEffect(() => {
    if (hasFirebaseLoaded && !hasCachedData) {
      if (key !== prevKey.current) {
        setIsLoading(true);

        if (!firestoreRef.current) {
          firestoreRef.current = window.Firebase.SDK;
        }

        const getData = async () => {
          if (firestoreRef.current) {
            const results = await effect(firestoreRef.current);

            if (key && enforcePermissions && !handlePermissionsCheck(results)) {
              dispatch(push('/404'));
            } else {
              batch(() => {
                setData(results);
                setIsLoading(false);

                if (upserter && !isEmpty(results)) {
                  dispatch(upserter(results));
                }
              });
            }
          }
        };

        if (key !== prevKey.current) {
          void getData();
        }
      }

      prevKey.current = key;
    }
  }, [isLoading, enforcePermissions, handlePermissionsCheck, effect, key, hasFirebaseLoaded, hasCachedData, dispatch, upserter]);

  useEffect(() => {
    if (data && key && enforcePermissions && !handlePermissionsCheck(data)) {
      dispatch(push('/404'));
    }
  }, [data, dispatch, enforcePermissions, handlePermissionsCheck, key]);

  return [cachedData || data || {} as C, isLoading];
};

/**
 * Runs the provided callback on an interval, given a delay in ms
 * @param  {Function} callback
 * @param  {number} delay
 * @return {void}
 */
export const useInterval = (callback: (...args: any[]) => any, delay: number) => {
  const savedCallback = useRef<typeof callback>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };

    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }

    return undefined;
  }, [delay]);
};
